import { HttpTelemetryEvent, TelemetryService } from './telemetry.service';

export class BeaconTelemetryService extends TelemetryService {
  constructor(private readonly sinkUrl: string) {
    super();
  }

  trackHttpRequest(event: HttpTelemetryEvent): void {
    const payload = this.toPayload(event);

    try {
      if (typeof navigator === 'undefined' || typeof navigator.sendBeacon !== 'function') {
        return;
      }

      const body = new Blob([JSON.stringify(payload)], {
        type: 'application/json',
      });

      navigator.sendBeacon(this.sinkUrl, body);
    } catch {
      // Intentionally swallow sink failures to avoid impacting user flows.
    }
  }

  private toPayload(event: HttpTelemetryEvent): Record<string, string | number | boolean> {
    return {
      type: 'http.request',
      method: event.method,
      url: sanitizeTelemetryUrl(event.url),
      status: Number.isFinite(event.status) ? event.status : 0,
      durationMs: Math.max(0, Math.round(event.durationMs)),
      succeeded: event.succeeded,
      ...(event.errorKind ? { errorKind: event.errorKind } : {}),
      timestamp: new Date().toISOString(),
    };
  }
}

export function resolveBeaconTelemetrySinkUrl(apiBaseUrl?: string): string {
  const fallbackPath = '/telemetry/http';

  if (!apiBaseUrl) {
    return fallbackPath;
  }

  try {
    const baseUrl = new URL(apiBaseUrl, window.location.origin);
    const normalizedPath = baseUrl.pathname.replace(/\/$/, '');
    return `${baseUrl.origin}${normalizedPath}${fallbackPath}`;
  } catch {
    return fallbackPath;
  }
}

export function sanitizeTelemetryUrl(url: string): string {
  try {
    const parsed = url.startsWith('/') ? new URL(url, window.location.origin) : new URL(url);
    return `${parsed.origin}${parsed.pathname}`;
  } catch {
    const withoutQuery = url.split('?')[0] ?? url;
    return withoutQuery.split('#')[0] ?? withoutQuery;
  }
}
